import React, { useEffect, useState } from 'react'
import { useSelector } from "src/stateManagement/redux";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Grid from "@mui/material/Grid2";
import moment from "moment";
import { getTranslationsForTable } from 'src/shared/traslations';
import { tableStyles } from 'src/shared/util/tableStyles';
import { Box } from '@mui/material';

export interface ITableProps {
    dataToTable: any[],
    setdataToTable: any,
    filteredData: any[],
    filters: any[]
}


const SubSurfaceJobs = ({ dataToTable, setdataToTable, filteredData, filters }: ITableProps) => {

    const { subsurfaceJobs } = useSelector((state) => state.location);

    // Función helper para renderizar headers en 2 líneas
    const renderTwoLineHeader = (line1: string, line2: string) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    width: '100%',
                    lineHeight: 1.2,
                }}
            >
                <Box component="span" sx={{ display: 'block' }}>{line1}</Box>
                <Box component="span" sx={{ display: 'block' }}>{line2}</Box>
            </Box>
        );
    };

    // Función para validar y formatear fechas
    const isValidDate = (value: any): boolean => {
        if (!value) return false;
        const date = moment(value);
        return date.isValid();
    };

    const formatDate = (value: any): string | null => {
        if (!isValidDate(value)) return null;
        try {
            return moment(value).format('DD-MM-YY');
        } catch (error) {
            return null;
        }
    };

    // Función para renderizar celdas de fecha con validación
    const renderDateCell = (params: any) => {
        const formattedDate = formatDate(params.value);
        return (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
                {formattedDate || '-'}
            </Box>
        );
    };

    const [dataColumns, setDataColumns] = useState<GridColDef[]>([
        { 
            field: 'fechaOpcionOptimizacion', 
            headerName: 'Fecha Creación', 
            width: 280, 
            flex: 1,  
            headerAlign: "center", 
            align: "center",
            type: 'date',
            renderHeader: () => renderTwoLineHeader('Fecha', 'Creación'),
            renderCell: renderDateCell,
            valueGetter: (value: any) => {
                // Si el valor ya es una fecha válida (Date object), retornarla
                if (value && typeof value === 'object' && value instanceof Date && !isNaN(value.getTime())) {
                    return value;
                }
                // Si es un string o número, intentar parsearlo
                if (!value) return null;
                const date = moment(value);
                return date.isValid() ? date.toDate() : null;
            },
        },
        { 
            field: 'programDate', 
            headerName: 'Fecha Programa', 
            width: 280, 
            flex: 1,  
            headerAlign: "center", 
            align: "center",
            type: 'date',
            renderHeader: () => renderTwoLineHeader('Fecha', 'Programa'),
            renderCell: renderDateCell,
            valueGetter: (value: any) => {
                if (value && typeof value === 'object' && value instanceof Date && !isNaN(value.getTime())) {
                    return value;
                }
                if (!value) return null;
                const date = moment(value);
                return date.isValid() ? date.toDate() : null;
            },
        },
        { 
            field: 'initDate', 
            headerName: 'Fecha Inicio', 
            width: 150, 
            flex: 1,  
            headerAlign: "center", 
            align: "center",
            type: 'date',
            renderHeader: () => renderTwoLineHeader('Fecha', 'Inicio'),
            renderCell: renderDateCell,
            valueGetter: (value: any) => {
                if (value && typeof value === 'object' && value instanceof Date && !isNaN(value.getTime())) {
                    return value;
                }
                if (!value) return null;
                const date = moment(value);
                return date.isValid() ? date.toDate() : null;
            },
        },
        { 
            field: 'finishedDate', 
            headerName: 'Fecha Culminación', 
            width: 170, 
            flex: 1,  
            headerAlign: "center", 
            align: "center",
            type: 'date',
            renderHeader: () => renderTwoLineHeader('Fecha', 'Culminación'),
            renderCell: renderDateCell,
            valueGetter: (value: any) => {
                if (value && typeof value === 'object' && value instanceof Date && !isNaN(value.getTime())) {
                    return value;
                }
                if (!value) return null;
                const date = moment(value);
                return date.isValid() ? date.toDate() : null;
            },
        },
        { 
            field: 'typeOfWork', 
            headerName: 'Tipo de Trabajo', 
            width: 200, 
            flex: 1,  
            headerAlign: "center", 
            align: "center",
            renderHeader: () => renderTwoLineHeader('Tipo de', 'Trabajo'),
        },           
        { 
            field: '', 
            headerName: 'Tipo de Trabajo Final', 
            minWidth: 200, 
            flex: 1,
            renderHeader: () => renderTwoLineHeader('Tipo de Trabajo', 'Final'),
        },      
        { 
            field: 'type_of_work_homologated', 
            headerName: 'Tipo de trabajo Homologado', 
            width: 200, 
            flex: 1,  
            headerAlign: "center", 
            align: "center",
            renderHeader: () => renderTwoLineHeader('Tipo de trabajo', 'Homologado'),
        },  
        { 
            field: 'targetProductionAfterWork', 
            headerName: '(BBPD)', 
            width: 100 
        },
    ])

    const paginationModel = { page: 0, pageSize: 10 };


    useEffect(() => {
        const datToTable: any[] = []

        let sortActivities: any[] = [...subsurfaceJobs].sort((a: any, b: any) => new Date(b.programDate).getTime() - new Date(a.programDate).getTime());

        for (let index = 0; index < sortActivities.length; index++) {
            const act = sortActivities[index];
            
            // Asegurar que siempre haya un id único válido para el DataGrid
            // El id debe ser un string único, nunca null
            let rowId: string;
            if (act.id !== null && act.id !== undefined && String(act.id).trim() !== '') {
                rowId = String(act.id);
            } else {
                // Generar un ID único temporal basado en el índice y timestamp
                rowId = `subsurface-job-${index}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            }
            
            const data:any = {
                id: rowId, // ID único para el DataGrid (siempre debe ser válido y único)
            }

            // Procesar fecha de creación (puede venir de diferentes campos)
            // Intentamos varios campos comunes para fecha de creación, pero NO usamos act.id
            const creationDateField = act.createdDate || act.createdAt || act.creationDate || act.dateCreated;
            if (creationDateField && isValidDate(creationDateField)) {
                data.createdDate = creationDateField;
            } else {
                data.createdDate = null;
            }

            // Procesar otros campos de fecha con validación
            const dateFields: string[] = ['programDate', 'initDate', 'finishedDate'];
            
            for (const field of dateFields) {
                if (act[field]) {
                    const dateValue = formatDate(act[field]);
                    if (dateValue) {
                        // Guardamos la fecha original para el valueGetter
                        data[field] = act[field];
                    } else {
                        // Si no es una fecha válida, no la incluimos o la marcamos como null
                        data[field] = null;
                    }
                } else {
                    data[field] = null;
                }
            }

            const doubleField:string[] = []

            for (const field of doubleField) {
                if (act[field]) {
                    try {
                        data[field] = parseFloat(act[field])
                    } catch (error) {
                        
                    }
                }
            }

            const stringField:string[] = ['status','typeOfWork','type_of_work_homologated','userGenerated','estimatedHours','grossProduction','targetProductionAfterWork','description']
            for (const field of stringField) {
                if (act[field]) {
                    try {
                        data[field] = act[field]
                    } catch (error) {
                    }
                }
            }

            datToTable.push(data)
        }

        setdataToTable(datToTable)

    }, [subsurfaceJobs])


    return (
        <Grid className="table-container">
            <DataGrid
                rows={getTranslationsForTable(dataToTable, 'es',["status"])}
                columns={dataColumns}
                initialState={{
                    pagination: { paginationModel }
                }}
                pageSizeOptions={[10, 20, 50, 100]}
                checkboxSelection={false}
                sx={{
                   ...tableStyles,
                   "& .MuiDataGrid-columnHeaderTitle": {
                       ...tableStyles["& .MuiDataGrid-columnHeaderTitle"],
                       whiteSpace: "normal",
                       lineHeight: 1.2,
                       minHeight: "auto",
                       padding: "8px 10px",
                   },
                   "& .MuiDataGrid-columnHeader": {
                       ...tableStyles["& .MuiDataGrid-columnHeader"],
                       minHeight: "auto !important",
                       height: "auto !important",
                   },
                }}
                getRowId={(row) => row.id}
            /> 
        </Grid>
    )
}

export default SubSurfaceJobs